import { HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs/operators';

import { GuestSessionService } from '../services/guest-session.service';
import { isSkilledApiRequest } from './skilled-api-request';

const GUEST_HEADER = 'x-guest-session-id';

/**
 * Attaches the persisted `x-guest-session-id` to every request that
 * targets the Skilled backend, and captures the response header so
 * the very first request (which has no id yet) gets a fresh one.
 *
 * Why scoped to backend URLs only:
 *  - We don't want to leak the guest id to 3rd-party APIs.
 *  - Tests / mocks pointing at localhost still get the header.
 */
export const guestSessionInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  if (!isSkilledApiRequest(req.url)) {
    return next(req);
  }

  const session = inject(GuestSessionService);
  const current = session.current();
  // Send credentials so the Skilled session cookie (set on `.aiskilled.co`)
  // flows between the landing and the main app — keeps the iframe in
  // `/campaign-preview/customer-profile` logged in after a landing signup.
  //
  // Not to the guest endpoints (`/api/public/*`: the chat, lead counts,
  // inquiries): they know the visitor by the header alone. Carrying the
  // cookie there only matters when the browser is signed in to the Skilled
  // app, and then the API's CSRF check refuses the guest's write with 403.
  const outbound = req.clone({
    withCredentials: !isGuestEndpoint(req.url),
    setHeaders: current ? { [GUEST_HEADER]: current } : {},
  });

  return next(outbound).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse)) return;
      const echoed = event.headers.get(GUEST_HEADER);
      if (echoed) {
        session.set(echoed);
      }
    }),
  );
};

/** `/api/public/*` on the Skilled API: keyed by `x-guest-session-id`, never by the session cookie. */
export function isGuestEndpoint(url: string): boolean {
  try {
    const base = typeof globalThis.location !== 'undefined' ? globalThis.location.origin : 'http://localhost';
    return new URL(url, base).pathname.startsWith('/api/public/');
  } catch {
    return false;
  }
}
